import { Colors, Icon, SelectOutlined } from '@amelio/ui-library';
import { useState } from 'react';

import { NotesResourcesTab } from './NotesResourcesTab';
import { SummaryTab } from './SummaryTab';

import type { CompetencyNote } from './NotesResourcesTab';
import type { EvaluationPeriodOption } from '@/pages/ManagerAssessment/Evaluation/types';
import type { CompanyValueCardProps } from '@/pages/shared/ManagerAssessment/CompanyValueCard';
import type { ProficiencyLevel } from '@/types/competencies/ProficiencyLevel';
import type { CompetencyGroupResult } from '@/types/manager-assessment/Competency';
import type { ProficiencyLevel as ProficiencyLevelValue } from '@/types/performance-profile';
import type { QueryStatus, UseMutateAsyncFunction } from '@tanstack/react-query';

import { useAmelioTranslations } from '@/hooks';
import { CompetencyAssessmentContent } from '@/pages/ManagerAssessment/Results/components/CompetencyAssessmentContent';

type ResultsTabType = 'summary' | 'competencies' | 'comparison' | 'notes';

interface ResultsTabNavigationProps {
    reviewInstanceId?: string;
    className?: string;
    dataTestId?: string;
    employeeId: string;
    notes: CompetencyNote[];
    notesStatus: QueryStatus;
    competencyGroup: CompetencyGroupResult[] | undefined;
    proficiencyLevels: ProficiencyLevel[];
    employeeName: string;
    isReadOnly?: boolean;
    selectedPeriod: string;
    periods: EvaluationPeriodOption[];
    fromDate?: Date;
    toDate?: Date;
    updateSkillProficiency: UseMutateAsyncFunction<
        void,
        unknown,
        {
            skillId: string;
            proficiency: ProficiencyLevelValue;
            comment: string;
            cycleInstanceId: string;
        },
        unknown
    >;
    isUpdatingSkillProficiency: boolean;
    companyValueCards: CompanyValueCardProps[];
}

export const ResultsTabNavigation = ({
    reviewInstanceId,
    employeeId,
    className = '',
    dataTestId = 'results-tab-navigation',
    notes,
    notesStatus,
    competencyGroup,
    proficiencyLevels,
    employeeName,
    isReadOnly = false,
    selectedPeriod,
    periods,
    fromDate,
    toDate,
    updateSkillProficiency,
    isUpdatingSkillProficiency,
    companyValueCards,
}: ResultsTabNavigationProps) => {
    const { t } = useAmelioTranslations();
    const [activeTab, setActiveTab] = useState<ResultsTabType>('summary');

    const tabs = [
        { label: t('postReviewResults.tabs.summary'), value: 'summary' as const },
        { label: t('postReviewResults.tabs.competencies'), value: 'competencies' as const },
        { label: t('postReviewResults.tabs.notesResources'), value: 'notes' as const },
    ];

    const handleSelectChange = (value: string) => {
        setActiveTab(value as ResultsTabType);
    };

    const renderTabContent = () => {
        switch (activeTab) {
            case 'summary':
                return (
                    <div className={'p-6'}>
                        <SummaryTab
                            cycleInstanceId={reviewInstanceId}
                            companyValueCards={companyValueCards}
                        />
                    </div>
                );
            case 'competencies':
                return (
                    <div className={'p-6 sm:p-10'}>
                        <CompetencyAssessmentContent
                            cycleInstanceId={reviewInstanceId ?? ''}
                            employeeId={employeeId}
                            proficiencyLevels={proficiencyLevels}
                            competencyGroup={competencyGroup}
                            employeeName={employeeName}
                            isReadOnly={isReadOnly}
                            updateSkillProficiency={updateSkillProficiency}
                            isUpdatingSkillProficiency={isUpdatingSkillProficiency}
                        />
                    </div>
                );
            case 'notes':
                return (
                    <div className={'p-6'}>
                        <NotesResourcesTab
                            notes={notes}
                            notesStatus={notesStatus}
                            employeeId={employeeId}
                            employeeFirstName={employeeName}
                            selectedPeriod={selectedPeriod}
                            periods={periods}
                            fromDate={fromDate}
                            toDate={toDate}
                        />
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <div
            className={`overflow-hidden rounded-2xl border border-gray-200 bg-white/60 backdrop-blur-2xl ${className}`}
            data-testid={dataTestId}
        >
            {/* Desktop tab navigation — shown on md and above, hidden on mobile */}
            <div
                className={
                    'scrollbar-glass hidden overflow-x-auto border-b border-gray-300 md:!flex'
                }
            >
                {tabs.map((tab) => (
                    <button
                        key={tab.value}
                        data-testid={`tab-${tab.value}`}
                        onClick={() => setActiveTab(tab.value)}
                        className={`focus-visible:ring-primary-800 relative flex-1 cursor-pointer border-b-2 border-transparent px-3 py-4 text-center text-sm font-medium whitespace-nowrap transition-all duration-300 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 sm:px-4 sm:py-5 md:px-6 md:py-8 md:text-lg ${
                            activeTab === tab.value
                                ? '!border-b-2 !border-[#0dd3bc] bg-[#0dd3bc0d] text-teal-500'
                                : 'text-gray-900'
                        }`}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* Mobile dropdown — shown below md, hidden on md and above */}
            <div className={'p-4 md:hidden'}>
                <SelectOutlined
                    value={activeTab}
                    options={tabs.map((tab) => ({ label: tab.label, value: tab.value }))}
                    onChange={handleSelectChange}
                    sort={'none'}
                    className={'!!text-base !border-teal-300/60'}
                    dataTestId={'results-tab-navigation-select'}
                    selectedIcon={
                        <Icon
                            name={'essential-checkmark'}
                            size={'lg'}
                            color={Colors.BLACK}
                            ariaHidden={true}
                        />
                    }
                    selectedIconPosition={'left'}
                />
            </div>

            {/* Tab Content */}
            <div className={'min-h-96'}>{renderTabContent()}</div>
        </div>
    );
};

export default ResultsTabNavigation;
